import { Compartment } from '@codemirror/state';
import {
  EditorView,
  ViewPlugin,
  type PluginValue,
  type ViewUpdate,
} from '@codemirror/view';
import { type MutableRefObject } from 'react';

/**
 * Attribute used to expose the placeholder text on the content element so it can
 * be rendered through a CSS `::after` pseudo-element.
 */
export const placeholderDataAttribute = 'data-placeholder';

/**
 * Creates an extension that exposes the placeholder text as the
 * `data-placeholder` attribute on the editor's content element while the
 * document is empty.
 *
 * We deliberately avoid CodeMirror's built-in `placeholder` extension: it
 * inserts a real widget node into the `contentEditable` area. When the editor is
 * empty and loses focus, the browser moves the native caret to the end of that
 * widget, which makes the placeholder look like actual content. Rendering the
 * text through a CSS `::after` pseudo-element (driven by this attribute) keeps
 * the caret at position 0.
 */
const createPlaceholderExtension = (placeholderText: string) =>
  ViewPlugin.fromClass(
    class implements PluginValue {
      private readonly contentDOM: HTMLElement;

      constructor(view: EditorView) {
        this.contentDOM = view.contentDOM;
        this.sync(view);
      }

      update(update: ViewUpdate): void {
        if (update.docChanged) {
          this.sync(update.view);
        }
      }

      destroy(): void {
        this.contentDOM.removeAttribute(placeholderDataAttribute);
      }

      private sync(view: EditorView): void {
        if (view.state.doc.length === 0) {
          this.contentDOM.setAttribute(
            placeholderDataAttribute,
            placeholderText,
          );
        } else {
          this.contentDOM.removeAttribute(placeholderDataAttribute);
        }
      }
    },
  );

/**
 * Configures the placeholder functionality on the editor.
 * @param placeholderText - string to be used as placeholder text for editor
 * @param viewRef - Ref object for the editor
 * @param placeholderCompartment - compartment used to reconfigure the placeholder extension
 */
export const configurePlaceholder = (
  placeholderText: string | undefined,
  viewRef: MutableRefObject<EditorView>,
  placeholderCompartment: Compartment,
): void => {
  const editorView = viewRef.current;
  editorView.dispatch({
    effects: placeholderCompartment.reconfigure(
      placeholderText ? createPlaceholderExtension(placeholderText) : [],
    ),
  });
};
